import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import axios from "const/axios";
import { usePaginatedSearch } from "hooks/usePaginatedSearch";
import { useEffect, useMemo, useState } from "react";
import { AiFillEdit } from "react-icons/ai";
import { IoClose } from "react-icons/io5";
import { toast } from "react-toastify";
import { getPaginatedClientData } from "utils/clientService";
import { mapClientToProfileFields } from "utils/clientProfileFields";
import { convertDateFormat } from "utils/dateFormat";
import AddClient from "views/admin/Clients/AddClient";

const SEARCH_PAGE_SIZE = 20;

const fetchClientPage = ({ page, size, search }) =>
  getPaginatedClientData({ pageIndex: page, pageSize: size, searchText: search });

const applyClientToForm = (prevState, value) => ({
  ...prevState,
  ...mapClientToProfileFields(value),
});

const normalizeClientDates = (client) => ({
  ...client,
  dob: convertDateFormat(client?.dob),
  dateOfIssue: convertDateFormat(client?.dateOfIssue),
  visaExpiry: convertDateFormat(client?.visaExpiry),
  passportExpiry: convertDateFormat(client?.passportExpiry),
});

export const ClientEditButton = ({ data, setData, variant = "icon", className = "" }) => {
  const [openEditModal, setOpenEditModal] = useState(false);
  const [clientRecord, setClientRecord] = useState(null);
  const [isLoadingClient, setIsLoadingClient] = useState(false);

  const clientId = data?.clientId ?? data?.id;

  const handleEdit = async () => {
    if (!clientId || isLoadingClient) return;
    setIsLoadingClient(true);
    try {
      const response = await axios.get(`clients/${clientId}`);
      const client = response?.data?.data;
      if (!client) {
        toast.error("Could not load this client's details.");
        return;
      }
      setClientRecord(normalizeClientDates(client));
      setOpenEditModal(true);
    } catch (error) {
      toast.error(error?.response?.data?.message ?? "Could not load this client's details.");
    } finally {
      setIsLoadingClient(false);
    }
  };

  const handleEdited = (updatedClient) => {
    setClientRecord(updatedClient);
    setData?.((prevState) => ({ ...prevState, ...mapClientToProfileFields(updatedClient) }));
  };

  if (!clientId) return null;

  return (
    <>
      {variant === "button" ? (
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={
            isLoadingClient ? <CircularProgress color="inherit" size={14} /> : <AiFillEdit />
          }
          onClick={handleEdit}
          disabled={isLoadingClient}
          className={className}
        >
          Edit client
        </Button>
      ) : (
        <Tooltip title="Edit client">
          <span>
            <IconButton
              onClick={handleEdit}
              className={className}
              size="small"
              disabled={isLoadingClient}
            >
              {isLoadingClient ? (
                <CircularProgress color="inherit" size={16} />
              ) : (
                <AiFillEdit className="text-primary" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Dialog
        open={openEditModal && Boolean(clientRecord)}
        onClose={() => setOpenEditModal(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Edit Client Details</h3>
            <IconButton onClick={() => setOpenEditModal(false)}>
              <IoClose className="text-primary" />
            </IconButton>
          </div>
        </DialogTitle>
        {clientRecord && (
          <AddClient
            importedData={clientRecord}
            setEditData={handleEdited}
            openEditModal={openEditModal}
            setOpenEditModal={setOpenEditModal}
          />
        )}
      </Dialog>
    </>
  );
};

const ClientDropdown = ({
  data,
  setData,
  onChange,
  onSelectAttempt,
  disabled = false,
  helperText = "",
  outerClassName = "",
  hideEditButton = false,
}) => {
  const { inputValue, setInputValue, debouncedSearch, options, loading, listboxProps } =
    usePaginatedSearch(fetchClientPage, { pageSize: SEARCH_PAGE_SIZE });

  const selectedValue = useMemo(() => {
    const id = data?.clientId;
    const name = data?.clientName ?? data?.name;
    if (!id || !name) return null;
    return {
      id,
      name,
      passportNumber: data.passportNumber,
      email: data.email,
    };
  }, [data?.clientId, data?.clientName, data?.name, data?.passportNumber, data?.email]);

  useEffect(() => {
    if (selectedValue?.name && !inputValue) {
      setInputValue(selectedValue.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValue?.name]);

  const mergedOptions = useMemo(() => {
    if (!selectedValue?.id) return options;
    if (options.some((client) => client.id === selectedValue.id)) return options;
    return [selectedValue, ...options];
  }, [options, selectedValue]);

  return (
    <div className={"relative w-full mb-3 " + outerClassName}>
      <label className="input-label">
        Select Client <span className="text-red-500">*</span>
      </label>
      <div className="flex items-start gap-4">
        <Autocomplete
          disabled={disabled}
          options={mergedOptions}
          loading={loading}
          filterOptions={(x) => x}
          value={selectedValue}
          inputValue={inputValue}
          onInputChange={(_, value, reason) => {
            if (reason === "reset") return;
            setInputValue(value);
          }}
          onChange={(_, value) => {
            if (onSelectAttempt?.(value) === false) return;
            onChange?.(value);
            if (setData) {
              setData((prevState) =>
                value
                  ? applyClientToForm(prevState, value)
                  : {
                      ...prevState,
                      clientId: null,
                      name: "",
                    }
              );
            }
            if (value?.name) setInputValue(value.name);
          }}
          getOptionLabel={(option) => option?.name || ""}
          isOptionEqualToValue={(option, value) =>
            option?.id === value?.id || option?.name === value?.name
          }
          renderOption={(props, option) => (
            <li {...props} key={option?.id ?? `${option?.email}-${option?.name}`}>
              <div className="flex items-center justify-between gap-2 w-full">
                <div className="flex items-center gap-2">
                  {option?.passportNumber
                    ? `${option?.name} (${option?.passportNumber})`
                    : option?.name || ""}
                </div>
              </div>
            </li>
          )}
          clearText="Clear"
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Client"
              required
              helperText={helperText || "Type to search clients"}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText={
            loading
              ? "Searching…"
              : debouncedSearch
              ? "No matching clients"
              : "Type to search clients"
          }
          ListboxProps={{
            style: {
              maxHeight: "180px",
            },
            ...listboxProps,
          }}
          className="flex-1"
        />
        {!hideEditButton && <ClientEditButton data={data} setData={setData} />}
      </div>
    </div>
  );
};

export default ClientDropdown;
